import { forwardRef, useImperativeHandle, useRef } from 'react';
import { Flex } from '@chakra-ui/react';
import { ajvResolver } from '@hookform/resolvers/ajv';
import { OutlineCard, TextArea, useToast } from '@morressier/alembic';
import {
  FormFieldFileUploadArray,
  FormFieldTextInput
} from '@publishing-engine/domain/types/article';
import { ISubmissionFile } from '@publishing-engine/domain/models';
import _ from 'lodash';
import { Controller, FieldError, FormProvider, useForm } from 'react-hook-form';
import { FieldLabel } from 'src/components/Field';
import { FormFieldMultipleFileUpload } from 'src/components/FormField';
import { IAuthorResponseValue } from 'src/components/Submission/decisionModals/AuthorResponseUpdateModal';
import { useSubmissionData } from 'src/contexts/SubmissionDataProvider';
import { useSubmissionFields } from 'src/hooks/useSubmissionFields';
import { generateValidationSchema } from 'src/utils/validation';
import FormFieldErrorMessage from '../../../../FormField/common/FormFieldErrorMessage';

type IProps = {
  submissionId: string;
  submissionContextId: string;
  manuscript?: ISubmissionFile | null;
  titlePage?: ISubmissionFile | null;
};

export type SubmitResponseHandle = {
  submit: () => Promise<void>;
  triggerValidation: () => Promise<boolean>;
  getValues: () => IAuthorResponseValue;
  hasUpdated: () => boolean;
};

const SubmitResponseForm = forwardRef<SubmitResponseHandle, IProps>(
  (
    { submissionId, submissionContextId, manuscript, titlePage },
    ref
  ) => {
    const toast = useToast();
    const { getFieldState, getOnChangeHandler } = useSubmissionFields();
    const { activeRevision, refetchSubmission } = useSubmissionData();

    const authorResponseField =
      getFieldState<FormFieldTextInput<'authorResponse'>>('AuthorResponse')!;
    const authorResponseAttachmentsField = getFieldState<
      FormFieldFileUploadArray<'authorResponseAttachments'>
    >('AuthorResponseAttachments');

    const { formFieldDefinition: authorResponseFFD } =
      authorResponseField ?? {};

    const { formFieldDefinition: authorAttachmentsFFD } =
      authorResponseAttachmentsField ?? {};

    const authorResponseOnChange = getOnChangeHandler('AuthorResponse');
    const authorResponseAttachmentsOnChange = getOnChangeHandler(
      'AuthorResponseAttachments'
    );

    const schema = generateValidationSchema({
      authorResponse: authorResponseFFD || null,
      authorResponseAttachments: authorAttachmentsFFD || null
    });
    const authorResponseFieldName = authorResponseFFD?.caseDataFields.main;
    const authorAttachmentsFieldName =
      authorAttachmentsFFD?.caseDataFields.main;

    const methods = useForm<IAuthorResponseValue>({
      mode: 'onChange',
      reValidateMode: 'onChange',
      defaultValues: _.merge(
        {},
        { [authorResponseFieldName!]: activeRevision?.authorResponse ?? '' },
        authorAttachmentsFieldName
          ? {
              [authorAttachmentsFieldName]:
                activeRevision?.authorResponseAttachments ?? []
            }
          : {}
      ),
      resolver: ajvResolver(schema)
    });
    const initialValues = useRef(methods.getValues());

    const {
      handleSubmit,
      formState: { errors },
      getValues,
      reset,
      trigger
    } = methods;

    const error = _.get(errors, authorResponseFieldName as string) as
      | FieldError
      | undefined;

   
    const existingFiles = {
      ...(manuscript?.name ? { RevisedManuscript: manuscript } : {}),
      ...(titlePage?.name ? { RevisedTitlePage: titlePage } : {}),
      ...(authorAttachmentsFieldName
        ? { [authorAttachmentsFieldName]: getValues().authorResponseAttachments ?? [] }
        : {})
    };

    const onSubmitLocal = async (data: IAuthorResponseValue) => {
      await authorResponseOnChange?.(data[authorResponseFieldName!], false);
      if (authorAttachmentsFieldName) {
        // Submit an empty array as well: otherwise removing the last attachment
        // skips this command and leaves the previously saved files intact.
        const attachments = data?.[authorAttachmentsFieldName] ?? [];
        await authorResponseAttachmentsOnChange?.(attachments, false);
      }
      await refetchSubmission?.();
      reset(getValues());

      toast({
        variant: 'success',
        title: 'Response Saved',
        description:
          'You can continue working on your response or submit it along with a revised manuscript.'
      });
    };
    useImperativeHandle(
      ref,
      () => ({
        submit: async () => {
          const fieldsToValidate = [
            ...(authorResponseFieldName ? [authorResponseFieldName] : []),
            ...(authorAttachmentsFieldName ? [authorAttachmentsFieldName] : [])
          ];

          const isValid = await trigger(fieldsToValidate as any);

          if (!isValid) {
            return Promise.reject(new Error('Validation failed in child form'));
          }

          await handleSubmit(async (data: IAuthorResponseValue) => {
            await onSubmitLocal(data);
          })();
        },

        triggerValidation: async () => {
          const fieldsToValidate = [
            ...(authorResponseFieldName ? [authorResponseFieldName] : []),
            ...(authorAttachmentsFieldName ? [authorAttachmentsFieldName] : [])
          ];

          return trigger(fieldsToValidate as any);
        },
        getValues: () => getValues(),
        hasUpdated: () => !_.isEqual(getValues(), initialValues.current)
      }),
      [
        authorResponseFieldName,
        authorAttachmentsFieldName,
        trigger,
        handleSubmit,
        onSubmitLocal,
        getValues
      ]
    );

    return (
      <>
        <FormProvider {...methods}>
          <form
            onSubmit={handleSubmit(async data => {
              await onSubmitLocal(data);
            })}
          >
            <Flex flexDirection="column" gap={8} padding={1} width="100%">
              <Flex flexDirection="column" gap={4}>
                {authorResponseFFD && (
                  <>
                    <Flex
                      flexDirection="column"
                      gap={2}
                      alignItems="flex-start"
                      id={authorResponseFieldName}
                      width="100%"
                    >
                      <OutlineCard padding={4} width="100%">
                        <Flex gap={2} flexDirection="column">
                          <FieldLabel
                            label={authorResponseFFD.params.labels.main}
                            subText={''}
                            isRequired={
                              authorResponseFFD.params.validations.required
                            }
                          />
                          <Controller
                            name={authorResponseFieldName!}
                            control={methods.control}
                            render={({ field }) => (
                              <TextArea
                                {...field}
                                value={field.value ?? ''}
                                height={`${
                                  (field.value?.length ?? 0) * 0.75
                                }px`}
                                isInvalid={Boolean(error)}
                                minHeight={100}
                                maxHeight={600}
                                placeholder="Provide a response to the review comments"
                              />
                            )}
                          />
                        </Flex>
                        <FormFieldErrorMessage config={authorResponseFFD} />
                      </OutlineCard>
                    </Flex>
                  </>
                )}
                <OutlineCard padding={4} width="100%">
                  {authorAttachmentsFFD && (
                    <Flex width="50%">
                      <FormFieldMultipleFileUpload
                        submissionId={submissionId}
                        submissionContextId={submissionContextId}
                        config={authorAttachmentsFFD}
                        isEditingAllowed={true}
                        submissionFields={existingFiles}
                      />
                    </Flex>
                  )}
                </OutlineCard>
              </Flex>
            </Flex>
          </form>
        </FormProvider>
      </>
    );
  }
);

export default SubmitResponseForm;
